
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import moment from 'moment';
import FilterWidget from '../shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { TableItemFormatter } from '../../elements/charts/helpers/tableItemFormatter';
import gdbx from '@/store/modules/gdbx';
import { COL, getTableName } from '@/worker/db/sqlHelpers';

@Component({
  components: {
    TableFilterable,
  },
})
export default class SalesFulfilledSalesOrder extends FilterWidget {
  public filterIds: Array<
    | 'date'
    | 'dateAsOf'
    | 'dateRange'
    | 'stockItems'
    | 'agents'
    | 'customers'
    | 'suppliers'
  > = [];

  public tableItems: any[] = [];
  public tableFields: any[] = [];
  public detailFields: any[] = [];

  public cachedRefHead: any = null;

  public get permissionIds(): PermissionsGroup[] {
    return ['sales', 'customers'];
  }

  public formatDays(value: number) {
    return value === -Infinity ? '-∞' : value;
  }

  public get exportFileName() {
    const formattedDate = [
      moment(this.selectedDateRange[0]).format('DD MMM YY'),
      moment(this.selectedDateRange[1]).format('DD MMM YY'),
    ];
    return 'Fulfilled Sales Order' + '_' + formattedDate[0] + ' to ' + formattedDate[1];
  }

  public returnStatus(deliveryDate: number, lastInvoiceDate: number) {
      if (deliveryDate === lastInvoiceDate) {
        return 'On-Time';
      }
      if (deliveryDate > lastInvoiceDate) {
        return 'Early';
      }
      if (deliveryDate < lastInvoiceDate) {
        return 'Late';
      }
      return '-';
  }

  public get expensiveHook() {
    const {
      selectedDateRange,
      selectedAsOfDate,
    } = this;
    return JSON.stringify([
      selectedDateRange,
      selectedAsOfDate,
    ]);
  }

  public async loadDetailForDoc(docNo: string): Promise<any[]> {
    if (!this.cachedRefHead) {
      this.cachedRefHead = await this._ensureTablesLoaded(['salesOrder'] as any[]);
    }

    const tableName = getTableName('salesOrder');
    const dateFilter = `TRY_CAST("DELIVERYDATE" AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
    const escaped = docNo.replace(/'/g, '\'\'');
    const lineData = await this.cachedRefHead.rawQuery(`
        SELECT ${COL.ITEMCODE} as itemCode,
            TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount,
            TRY_CAST(${COL.QUANTITY} AS DOUBLE) as quantity,
            TRY_CAST(${COL.DISCOUNT} AS DOUBLE) as discount,
            TRY_CAST("DELIVERYDATE" AS BIGINT) as deliveryDate,
            TRY_CAST("LASTINVOICEDATE" AS BIGINT) as lastInvoiceDate
        FROM ${tableName}
        WHERE ${dateFilter} AND ${COL.DOCNO} = '${escaped}'
    `);

    return lineData
        .filter((row: any) => (Number(row.quantity) || 0) > 0)
        .map((row: any) => {
          const amt = Number(row.amount) || 0;
          const qty = Number(row.quantity) || 0;
          const deliveryDate = Number(row.deliveryDate) || 0;
          const lastInvoiceDate = Number(row.lastInvoiceDate) || 0;
          const hasValidDate = lastInvoiceDate > 0;

          return {
            status: hasValidDate
                ? this.returnStatus(deliveryDate, lastInvoiceDate)
                : '-',
            unit_price: qty ? amt / qty : 0,
            discount: Number(row.discount) ? Number(row.discount) : null,
            item_desc: gdbx.stockItemNames[String(row.itemCode)] || 'Unknown',
            quantity: qty,
            amount: amt,
            expected_delivery: moment(deliveryDate).format('DD/MM/YYYY'),
            actual_delivery: hasValidDate
                ? moment(lastInvoiceDate).format('DD/MM/YYYY')
                : '-',
            days: hasValidDate
                ? Math.ceil(moment.duration(lastInvoiceDate - deliveryDate).asDays())
                : -Infinity,
          };
        });
  }

  public async expensiveCalc() {
    const jobId = this.currentJobId;
    this.generateLoadingText(0.2, 0.4);

    const queryHead = await this._ensureTablesLoaded(['salesOrder'] as any[]);
    this.checkInterrupted(jobId);
    this.generateLoadingText(0.4, 0.9);

    const tableName = getTableName('salesOrder');
    const dateFilter = `TRY_CAST("DELIVERYDATE" AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
    this.cachedRefHead = queryHead;

    // Only docs where ALL items are fulfilled (total osQty = 0)
    const summaryData = await queryHead.rawQuery(`
        SELECT ${COL.DOCNO} as docNo,
            MIN(${COL.ITEMCODE}) as itemCode,
            MIN(TRY_CAST(${COL.DOCDATE} AS BIGINT)) as docDate,
            MIN(TRY_CAST("DELIVERYDATE" AS BIGINT)) as deliveryDate,
            MIN(TRY_CAST("LASTINVOICEDATE" AS BIGINT)) as lastInvoiceDate,
            MIN(${COL.AGENT}) as agent,
            MIN(${COL.CODE}) as code
        FROM ${tableName}
        WHERE ${dateFilter}
        GROUP BY ${COL.DOCNO}
        HAVING SUM(TRY_CAST("OSQTY" AS DOUBLE)) = 0
            AND SUM(TRY_CAST(${COL.QUANTITY} AS DOUBLE)) > 0
    `);

    const result: any[] = [];

    for (const row of summaryData) {
        const docNo = String(row.docNo);
        const deliveryDate = Number(row.deliveryDate) || 0;
        const lastInvoiceDate = Number(row.lastInvoiceDate) || 0;
        const hasValidDate = lastInvoiceDate > 0;

        const days = hasValidDate
            ? Math.ceil(moment.duration(lastInvoiceDate - deliveryDate).asDays())
            : -Infinity;

        result.push({
            so_no: docNo,
            item_code: String(row.itemCode),
            so_date: moment(Number(row.docDate)).format('DD/MM/YYYY'),
            expected_delivery: moment(deliveryDate).format('DD/MM/YYYY'),
            actual_delivery: hasValidDate
                ? moment(lastInvoiceDate).format('DD/MM/YYYY')
                : '-',
            numberDate: deliveryDate,
            status: hasValidDate
                ? this.returnStatus(deliveryDate, lastInvoiceDate)
                : '-',
            days,
            items: [],
            agent: String(row.agent),
            customer: gdbx.customerNames[String(row.code)] || '',
        });
    }
    this.generateLoadingText(0.95);

    this.tableFields = [
      {
        key: 'status',
        sortable: false,
      },
      {
        key: 'days',
        sortable: false,
        formatter: this.formatDays,
        numeric: true,
      },
      {
        key: 'expected_delivery',
        sortable: false,
      },
      {
        key: 'actual_delivery',
        sortable: false,
      },
      {
        key: 'customer',
        sortable: false,
      },
      {
        key: 'agent',
        sortable: false,
      },
      {
        key: 'so_date',
        sortable: false,
      },
      {
        key: 'so_no',
        sortable: false,
      },
    ];

    this.detailFields = [
      {
        key: 'status',
        sortable: false,
      },
      {
        key: 'days',
        sortable: false,
        formatter: this.formatDays,
        numeric: true,
      },
      {
        key: 'expected_delivery',
        sortable: false,
      },
      {
        key: 'actual_delivery',
        sortable: false,
      },
      {
        key: 'item_desc',
        sortable: false,
      },
      {
        key: 'quantity',
        sortable: false,
        formatter: TableItemFormatter.basicInteger,
      },
      {
        key: 'unit_price',
        sortable: false,
        formatter: TableItemFormatter.currencySplit,
      },
      {
        key: 'amount',
        sortable: false,
        formatter: TableItemFormatter.currencySplit,
      },
    ];

    this.checkInterrupted(jobId);

    this.tableItems = result;

    this.saveHistory('tableItems', 'tableFields', 'detailFields');
  }
}
